import React, { useEffect, useMemo, useState } from "react";
import { getTrainingDays, getTrainingDetails, duplicateTrainingMultiple } from "../../services/authService";

// Retourne le lundi (à 00:00 heure locale) de la semaine contenant `date`.
function startOfWeek(date) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	const day = d.getDay(); // 0=Sun … 6=Sat
	const diff = day === 0 ? -6 : 1 - day;
	d.setDate(d.getDate() + diff);
	return d;
}

function addDays(date, n) {
	const d = new Date(date);
	d.setDate(d.getDate() + n);
	return d;
}

function fmtRange(monday) {
	const sunday = addDays(monday, 6);
	const opt = { day: "2-digit", month: "short" };
	return `${monday.toLocaleDateString("fr-FR", opt)} → ${sunday.toLocaleDateString("fr-FR", opt)}`;
}

function fmtInput(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

// Récupère toutes les séances (non-templates) de la semaine commençant le lundi `weekStart`
// pour un élève, en interrogeant les mois concernés.
async function fetchWeekTrainings(studentId, weekStart) {
	const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
	// Cache par (year, month) pour ne pas appeler getTrainingDays plusieurs fois pour le même mois
	const monthCache = new Map();
	const results = [];
	for (const day of days) {
		const y = day.getFullYear();
		const m = day.getMonth() + 1;
		const key = `${y}-${m}`;
		if (!monthCache.has(key)) {
			const res = await getTrainingDays(studentId, y, m);
			monthCache.set(key, res.success ? (res.trainingDays || []) : []);
		}
		const dayNum = day.getDate();
		if (!monthCache.get(key).includes(dayNum)) continue;

		const details = await getTrainingDetails(studentId, y, m, dayNum);
		if (details.success && details.trainings) {
			details.trainings.forEach((t) => {
				if (t.estTemplate || t.EstTemplate) return;
				results.push({
					id: t.id,
					nom: t.nom || "Séance",
					date: new Date(day).setHours(0, 0, 0, 0),
					nbExos: (t.exercices || []).filter((ex) => ex.supprime !== 1).length,
				});
			});
		}
	}
	return results;
}

export default function DuplicateWeekModal({ student, initialTargetDate, onClose, onDone, onNotif }) {
	// Semaine cible = celle de initialTargetDate (défaut aujourd'hui)
	const targetMonday = useMemo(() => startOfWeek(initialTargetDate || new Date()), [initialTargetDate]);

	const [sourceMonday, setSourceMonday] = useState(() => addDays(targetMonday, -7));
	const [customTargetMonday, setCustomTargetMonday] = useState(targetMonday);
	const [loading, setLoading] = useState(false);
	const [trainings, setTrainings] = useState([]);
	// Ids des séances sélectionnées pour duplication (défaut : toutes cochées à
	// chaque rechargement). Set pour lookups O(1) sans deps tierces.
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");

	// Charge les séances de la semaine source à chaque changement, et coche tout
	// par défaut (le cas 99% = "je duplique toute la semaine").
	useEffect(() => {
		if (!student?.id) return;
		let cancelled = false;
		setLoading(true);
		setError("");
		fetchWeekTrainings(student.id, sourceMonday).then((list) => {
			if (cancelled) return;
			setTrainings(list);
			setSelectedIds(new Set(list.map((t) => t.id)));
			setLoading(false);
		}).catch(() => {
			if (cancelled) return;
			setError("Erreur lors du chargement des séances");
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [student?.id, sourceMonday]);

	const toggleSelected = (id) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};
	const selectAll = () => setSelectedIds(new Set(trainings.map((t) => t.id)));
	const selectNone = () => setSelectedIds(new Set());

	// Offset (en jours) entre source Monday et target Monday
	const dayOffset = useMemo(() => {
		const ms = 24 * 3600 * 1000;
		const s = new Date(sourceMonday); s.setHours(0, 0, 0, 0);
		const t = new Date(customTargetMonday); t.setHours(0, 0, 0, 0);
		return Math.round((t.getTime() - s.getTime()) / ms);
	}, [sourceMonday, customTargetMonday]);

	const previews = trainings.map((t) => {
		const src = new Date(t.date);
		const dst = addDays(src, dayOffset);
		return { ...t, targetDate: dst };
	});
	const selectedPreviews = previews.filter((p) => selectedIds.has(p.id));
	const nSelected = selectedPreviews.length;
	const nTotal = previews.length;

	const handleConfirm = async () => {
		if (!student?.id) return;
		if (nSelected === 0) return;
		setSubmitting(true);
		setError("");
		let successCount = 0;
		let failCount = 0;
		for (const p of selectedPreviews) {
			const targetTs = p.targetDate.getTime();
			const res = await duplicateTrainingMultiple(student.id, p.id, [targetTs]);
			if (res.success) successCount++;
			else failCount++;
		}
		setSubmitting(false);
		if (failCount === 0) {
			onNotif?.(`${successCount} séance${successCount > 1 ? "s" : ""} dupliquée${successCount > 1 ? "s" : ""}`);
			onDone?.();
			onClose?.();
		} else {
			setError(`${successCount} réussies, ${failCount} en échec`);
			if (successCount > 0) onDone?.();
		}
	};

	// Presets pour la semaine source
	const sourcePresets = [
		{ label: "Semaine dernière", value: addDays(targetMonday, -7) },
		{ label: "Il y a 2 semaines", value: addDays(targetMonday, -14) },
		{ label: "Il y a 3 semaines", value: addDays(targetMonday, -21) },
		{ label: "Il y a 4 semaines", value: addDays(targetMonday, -28) },
	];

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			{/* Taille fixe (largeur + hauteur) pour éviter que la modale se redimensionne
			    quand on change de semaine source (le nombre de séances change et donc
			    la hauteur de la liste). La liste des séances scrolle en interne. */}
			<div className="mens-modal dup-week-modal" onClick={(e) => e.stopPropagation()}>
				<div className="mens-modal-header dup-week-modal-header">
					<h3>Dupliquer une semaine · {student?.prenom || "Élève"}</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				{/* Zone haute figée : sélecteurs source + cible, toujours visibles même
				    quand la liste des séances est longue et scrolle. */}
				<div className="dup-week-modal-controls">
				<div style={{ marginTop: 0 }}>
					<label className="mens-modal-label" style={{ display: "block", marginBottom: 6 }}>
						Semaine source
					</label>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
						{sourcePresets.map((p) => (
							<button
								key={p.label}
								type="button"
								className={`editor-panel-templates-folderpill ${sourceMonday.getTime() === p.value.getTime() ? "active" : ""}`}
								onClick={() => setSourceMonday(p.value)}
							>
								{p.label}
							</button>
						))}
					</div>
					<input
						type="date"
						value={fmtInput(sourceMonday)}
						onChange={(e) => {
							if (!e.target.value) return;
							const d = new Date(e.target.value + "T00:00:00");
							setSourceMonday(startOfWeek(d));
						}}
						className="mens-modal-input"
					/>
					<div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>
						Semaine du {fmtRange(sourceMonday)}
					</div>
				</div>

				<div style={{ marginTop: 16 }}>
					<label className="mens-modal-label" style={{ display: "block", marginBottom: 6 }}>
						Semaine cible
					</label>
					<input
						type="date"
						value={fmtInput(customTargetMonday)}
						onChange={(e) => {
							if (!e.target.value) return;
							const d = new Date(e.target.value + "T00:00:00");
							setCustomTargetMonday(startOfWeek(d));
						}}
						className="mens-modal-input"
					/>
					<div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>
						Semaine du {fmtRange(customTargetMonday)}
					</div>
				</div>
				</div>
				{/* Zone médiane scrollable : récap + liste des séances. flex:1 pour
				    absorber tout l'espace restant, overflow-y:auto pour scroller
				    sans faire varier la hauteur totale de la modale. */}
				<div className="dup-week-modal-scroll">
				<div style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
					<div className="dup-week-recap-head">
						<span className="dup-week-recap-title">
							{loading
								? "Chargement des séances…"
								: nTotal === 0
									? "Aucune séance sur cette semaine"
									: (
										<>
											Récap · <strong>{nSelected}</strong> / {nTotal} séance{nTotal > 1 ? "s" : ""} sélectionnée{nSelected > 1 ? "s" : ""}
										</>
									)
							}
						</span>
						{!loading && nTotal > 0 && (
							<div className="dup-week-recap-actions">
								<button type="button" className="dup-week-toggle-btn" onClick={selectAll} disabled={nSelected === nTotal}>
									Tout
								</button>
								<button type="button" className="dup-week-toggle-btn" onClick={selectNone} disabled={nSelected === 0}>
									Aucun
								</button>
							</div>
						)}
					</div>

					{!loading && nTotal === 0 && (
						<div className="dup-week-empty">
							Il n'y a aucune séance à dupliquer sur cette semaine. Choisis une autre semaine source ci-dessus.
						</div>
					)}

					{nTotal > 0 && (
						<div className="dup-week-list">
							{previews.map((p) => {
								const checked = selectedIds.has(p.id);
								return (
									<label
										key={p.id}
										className={`dup-week-item ${checked ? "checked" : ""}`}
									>
										<input
											type="checkbox"
											checked={checked}
											onChange={() => toggleSelected(p.id)}
											disabled={submitting}
											className="dup-week-item-check"
										/>
										<div className="dup-week-item-info">
											<span className="dup-week-item-name">{p.nom}</span>
											<span className="dup-week-item-meta">{p.nbExos} exo{p.nbExos > 1 ? "s" : ""}</span>
										</div>
										<div className="dup-week-item-dates">
											<span className="dup-week-item-src">
												{new Date(p.date).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}
											</span>
											<span className="dup-week-item-arrow" aria-hidden="true">→</span>
											<span className={`dup-week-item-dst ${checked ? "active" : ""}`}>
												{p.targetDate.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}
											</span>
										</div>
									</label>
								);
							})}
						</div>
					)}
				</div>
				</div>

				{error && <div className="mens-modal-error" style={{ marginTop: 8 }}>{error}</div>}

				<div className="mens-modal-actions dup-week-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={submitting}>Annuler</button>
					<button
						className="mens-btn primary"
						onClick={handleConfirm}
						disabled={loading || submitting || nSelected === 0}
						title={nSelected === 0 ? "Coche au moins une séance à dupliquer" : ""}
					>
						{submitting
							? "En cours…"
							: nSelected === 0
								? "Aucune sélection"
								: `Confirmer · dupliquer ${nSelected} séance${nSelected > 1 ? "s" : ""}`
						}
					</button>
				</div>
			</div>
		</div>
	);
}
